// ============================================================
// FILE: src/components/InquiryForm.jsx  →  THE INQUIRY FORM
// The "Request Quotation" form used on the Contact page,
// product pages and blog articles.
// HOW SUBMISSIONS WORK:
//   Every submission is emailed straight to the company inbox
//   (export@evolvetrade.in) via Web3Forms — no server needed,
//   works on any static hosting (Hostinger shared, etc.).
//   The Web3Forms key lives in src/data/site.js (WEB3FORMS_ACCESS_KEY).
// ============================================================

import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { WEB3FORMS_ACCESS_KEY } from "../data/site";

export default function InquiryForm({ defaultProduct = "", type = "general", compact = false, testIdPrefix = "inquiry" }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", country: "", product: defaultProduct, message: "" });
  const [status, setStatus] = useState("idle");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    const payload = { ...form, type };
    // Web3Forms emails every submission straight to export@evolvetrade.in.
    const data = new FormData();
    data.append("access_key", WEB3FORMS_ACCESS_KEY);
    data.append("subject", `New ${type} inquiry from ${form.name} — Evolve Website`);
    data.append("from_name", "Evolve Trade & Logistics Website");
    Object.entries(payload).forEach(([k, v]) => v && data.append(k, v));
    const ok = await fetch("https://api.web3forms.com/submit", {
      method: "POST",
      body: data, // browser sets the multipart boundary automatically
      headers: { Accept: "application/json" },
    })
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        return r.ok && j.success === true;
      })
      .catch(() => false);
    if (ok) {
      setStatus("success");
      toast.success("Inquiry received. Our trade desk will respond within 24 hours.");
    } else {
      setStatus("idle");
      toast.error("Submission failed. Please try again or reach us on WhatsApp.");
    }
  };

  return (
    <div className="relative" data-testid={`${testIdPrefix}-form-wrapper`}>
      <AnimatePresence mode="wait">
        {status === "success" ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            className="flex flex-col items-center justify-center rounded-2xl border border-teal-500/30 bg-teal-500/5 px-8 py-14 text-center"
            data-testid={`${testIdPrefix}-success`}
          >
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 12, delay: 0.1 }}>
              <CheckCircle2 className="h-14 w-14 text-teal-500" />
            </motion.div>
            <h3 className="mt-5 font-display text-2xl font-semibold">Inquiry received</h3>
            <p className="mt-2 max-w-sm text-sm text-muted-foreground">
              Our trade desk will respond within 24 hours with a detailed quotation. For urgent requirements, message us on WhatsApp.
            </p>
            <button onClick={() => { setStatus("idle"); setForm({ name: "", email: "", company: "", phone: "", country: "", product: defaultProduct, message: "" }); }} className="btn-secondary mt-6 !py-2.5" data-testid={`${testIdPrefix}-send-another`}>
              Send another inquiry
            </button>
          </motion.div>
        ) : (
          <motion.form key="form" exit={{ opacity: 0, scale: 0.98 }} onSubmit={submit} className={`grid gap-4 ${compact ? "" : "sm:grid-cols-2"}`} data-testid={`${testIdPrefix}-form`}>
            <input required aria-label="Full name" placeholder="Full name *" value={form.name} onChange={set("name")} className="field" data-testid={`${testIdPrefix}-name-input`} />
            <input required type="email" aria-label="Work email" placeholder="Work email *" value={form.email} onChange={set("email")} className="field" data-testid={`${testIdPrefix}-email-input`} />
            <input aria-label="Company" placeholder="Company" value={form.company} onChange={set("company")} className="field" data-testid={`${testIdPrefix}-company-input`} />
            <input aria-label="Phone or WhatsApp" placeholder="Phone / WhatsApp" value={form.phone} onChange={set("phone")} className="field" data-testid={`${testIdPrefix}-phone-input`} />
            <input aria-label="Country" placeholder="Country" value={form.country} onChange={set("country")} className="field" data-testid={`${testIdPrefix}-country-input`} />
            <input aria-label="Product of interest" placeholder="Product of interest" value={form.product} onChange={set("product")} className="field" data-testid={`${testIdPrefix}-product-input`} />
            <textarea
              required
              aria-label="Your requirement"
              placeholder="Tell us about your requirement — volume, destination, timeline *"
              value={form.message}
              onChange={set("message")}
              rows={compact ? 4 : 5}
              className="field sm:col-span-2"
              data-testid={`${testIdPrefix}-message-input`}
            />
            <div className="sm:col-span-2">
              {/* Web3Forms spam honeypot — invisible to humans, bots fill it */}
              <input type="checkbox" name="botcheck" tabIndex="-1" autoComplete="off" style={{ display: "none" }} aria-hidden="true" />
              <button type="submit" disabled={status === "loading"} className="btn-primary w-full sm:w-auto" data-testid={`${testIdPrefix}-submit-button`}>
                {status === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {status === "loading" ? "Sending…" : "Request Quotation"}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
}
